import React, { useCallback, useEffect, useState } from "react";
import * as Bananas from "bananas-commerce-admin";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useSnackbar } from "notistack";

import { ObituaryMediaDiff } from "@/extensions/mediahouse/types";

import ColoredText from "./ColoredText";

export type ReviewAdDialogType = "new" | "edit" | "cancellation";
export interface ReviewAdDialogProps {
  obituaryId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  refresh: () => void;
  type: ReviewAdDialogType;
}

const ReviewAdDialog: React.FC<ReviewAdDialogProps> = ({
  open,
  setOpen,
  obituaryId,
  refresh,
  type,
}) => {
  const isCancellation = type === "cancellation";
  const [isApproving, setIsApproving] = useState<boolean>(true);
  const [comment, setComment] = useState<string>("");
  const api = Bananas.useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [diff, setDiff] = useState<ObituaryMediaDiff>();
  const [showDiff, setShowDiff] = useState<boolean>(false);
  const approveToast = isCancellation
    ? "Avbokningen har godkänts."
    : "Ändringen har godkänts.";
  const rejectToast = isCancellation
    ? "Avbokningen har refuserats."
    : "Ändringen har refuserats.";
  const approveErrorToast = isCancellation
    ? "Kunde inte godkänna avbokningen."
    : "Kunde inte godkänna ändringen.";
  const rejectErrorToast = isCancellation
    ? "Kunde inte refusera avbokningen."
    : "Kunde inte refusera ändringen.";
  const description = isCancellation
    ? "Annonsen är avbokad efter stopptid"
    : "Annonsen är ändrad efter stopptid och måste granskas.";
  const approveLabel = isCancellation
    ? "Godkänn avbokning"
    : "Godkänn sen ändring";
  const rejectLabel = isCancellation
    ? "Refusera avbokning"
    : "Refusera sen ändring";

  const handleApprove = useCallback(async () => {
    const response = await api.operations[
      "mediahouse.obituaries:approve"
    ]?.call({
      params: { obituary_id: obituaryId },
    });

    if (response?.ok) {
      enqueueSnackbar(approveToast, { variant: "success" });
      setOpen(false);
    } else {
      enqueueSnackbar(approveErrorToast, { variant: "error" });
    }
  }, [
    api,
    obituaryId,
    approveErrorToast,
    approveToast,
    enqueueSnackbar,
    setOpen,
  ]);

  const handleReject = useCallback(async () => {
    const response = await api.operations["mediahouse.obituaries:reject"]?.call(
      {
        params: { obituary_id: obituaryId },
        body: {
          rejection_message: comment || "No comment.",
        },
      },
    );

    if (response?.ok) {
      enqueueSnackbar(rejectToast, { variant: "success" });
      setOpen(false);
    } else {
      enqueueSnackbar(rejectErrorToast, { variant: "error" });
    }
  }, [
    api,
    obituaryId,
    comment,
    enqueueSnackbar,
    rejectErrorToast,
    rejectToast,
    setOpen,
  ]);

  const handleSubmit = useCallback(async () => {
    if (isApproving) {
      await handleApprove();
      refresh();
    } else {
      await handleReject();
    }
  }, [isApproving, handleApprove, handleReject, refresh]);

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const fetchDiff = useCallback(async () => {
    const response = await api.operations["mediahouse.obituaries:diff"]?.call({
      params: { obituary_id: obituaryId },
    });

    if (response?.ok) {
      const data: ObituaryMediaDiff = await response.json();
      setDiff(data);

      const hasData = data.edit_message || data.changes.length > 0;
      const isEditType = type === "edit";
      if (hasData || isEditType) {
        setShowDiff(true);
        return;
      }
    }
  }, [api, obituaryId, type]);

  useEffect(() => {
    if (type === "cancellation" || diff != null || !open) return;
    fetchDiff();
  }, [open, diff, fetchDiff, type]);

  useEffect(() => {
    setDiff(undefined);
    setShowDiff(false);
  }, [type, obituaryId]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 7,
        },
      }}
      onClose={handleCancel}
    >
      <DialogContent sx={{ p: 0 }}>
        <Stack direction="row">
          <Stack
            borderColor="divider !important"
            borderRight={1}
            flex={1}
            spacing={2}
          >
            <Stack pt={3} px={3}>
              <Typography gutterBottom fontWeight={500} variant="h5">
                Granska sen ändring
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {description}
              </Typography>
            </Stack>
            <Divider />
            {showDiff && (
              <>
                <Stack mt={2} px={3} spacing={3}>
                  {diff?.changes.map((change) => (
                    <Box key={change.item_name}>
                      <Typography
                        gutterBottom
                        color="text.secondary"
                        variant="body2"
                      >
                        {change.item_name}
                      </Typography>
                      <Stack alignItems="center" direction="row" spacing={2}>
                        <ColoredText color="tangerine">
                          {change.old_value}{" "}
                        </ColoredText>
                        <Typography color="text.secondary">→</Typography>
                        <ColoredText color="green">
                          {change.new_value}
                        </ColoredText>
                      </Stack>
                    </Box>
                  ))}

                  {diff?.edit_message && (
                    <Box>
                      <Typography
                        gutterBottom
                        color="text.secondary"
                        variant="body2"
                      >
                        Kommentar
                      </Typography>
                      <Typography variant="body2">
                        {diff?.edit_message ?? "Ingen kommentar."}
                      </Typography>
                    </Box>
                  )}
                </Stack>
                <Divider />
              </>
            )}
            <Stack px={3}>
              <FormControl>
                <RadioGroup
                  defaultValue="approve"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontWeight: 500,
                      fontSize: 14,
                    },
                  }}
                  value={isApproving ? "approve" : "reject"}
                  onChange={(e) => setIsApproving(e.target.value === "approve")}
                >
                  <FormControlLabel
                    control={<Radio size="small" />}
                    label={approveLabel}
                    value="approve"
                  />
                  <FormControlLabel
                    control={<Radio size="small" />}
                    label={rejectLabel}
                    value="reject"
                  />
                </RadioGroup>
              </FormControl>

              {!isApproving && !isCancellation && (
                <Stack pt={2}>
                  <TextField
                    multiline
                    helperText="Beställaren kommer att få ett epost-meddelande med anledningen."
                    id="comment"
                    label="Anledning för refusering"
                    name="comment"
                    rows={2}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </Stack>
              )}
            </Stack>
            <Stack
              alignItems="flex-end"
              direction="row"
              flex={1}
              justifyContent="flex-end"
              p={3}
            >
              <Button variant="text" onClick={handleCancel}>
                Avbryt
              </Button>
              <Button variant="text" onClick={handleSubmit}>
                Skicka
              </Button>
            </Stack>
          </Stack>

          {/* Before / after previews (previously checking type === "edit" didn't work, but
            checking if we have an old image instead of the type being edit should work the same) */}
          {diff?.old_preview_url && (
            <Stack
              alignItems="center"
              bgcolor="grey.100"
              direction="row"
              flex={1}
              justifyContent="center"
              p={3}
              spacing={2}
            >
              <Stack spacing={1}>
                <ColoredText color="tangerine" textAlign="center">
                  Innan
                </ColoredText>
                <Box>
                  <img
                    alt="Förhandsvisning före ändring"
                    src={diff?.old_preview_url}
                    style={{ width: "100%" }}
                  />
                </Box>
              </Stack>
              <Stack spacing={1}>
                <ColoredText color="green" textAlign="center">
                  Efter
                </ColoredText>
                <Box>
                  <img
                    alt="Förhandsvisning efter ändring"
                    src={diff?.new_preview_url}
                    style={{ width: "100%" }}
                  />
                </Box>
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewAdDialog;
